import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Grid } from '@mui/material'

import { faSync } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import SectionTitle from 'components/Common/SectionTitle'
import DaySpan from 'components/Common/DaySpan'
import ConnectButton from 'components/Common/Button/ConnectButton'
import AlertBadge from 'components/Common/AlertBadge'
import ZendeskLink from 'components/Common/ZendeskLink'
import { Link, Typography } from '@mui/material'

import CloseIcon from '@mui/icons-material/Close'
import actionTypes from 'store/actions'

import TransactionsChart from './components/Charts/TransactionsChart'
import SalesVolumeChart from './components/Charts/SalesVolumeChart'
import DisputeChart from './components/Charts/DisputeChart'

import SalesOverviewPlaceholder from './components/SalesOverviewPlaceholder'
import DisputesTable from './components/Tables/SalesTable/DisputesTable'
import TransactionTable from './components/Tables/SalesTable/TransactionTable'

const TableType = {
  TRANSACTIONS: 'Transactions',
  SALE_VOLUME: 'Sales Volume',
  DISPUTES: 'Disputes',
}

function SalesDialog() {
  const dispatch = useDispatch()
  const codatConnected = useSelector(state => state.appReducer.commerceCodatConnected)

  const dayspanSelected = useSelector(state => state.salesReducer.salesOverviewDaySpan)
  const [activeTableType, setActiveTableType] = useState(TableType.TRANSACTIONS)
  const [onRefresh, setOnRefresh] = useState(null)

  // Filter States
  const [trxFilter, setTrxFilter] = useState(null)
  const [disputesFilter, setDisputesFilter] = useState(null)

  const onChangeTable = useCallback(
    value => {
      if (value !== activeTableType) {
        setActiveTableType(value)
        dispatch({ type: actionTypes.RESETSALESDATA })
      }
    },
    [setActiveTableType, dispatch, activeTableType],
  )

  const onDaySpanClicked = async days => {
    dispatch({ type: actionTypes.SETSALESOVERVIEWDAYSPAN, data: days })
  }

  const handleRefresh = async () => {
    if (codatConnected) {
      onDaySpanClicked(dayspanSelected)
    }
  }

  useEffect(() => {
    if (onRefresh === null) return
    handleRefresh().then()
    // eslint-disable-next-line
  }, [onRefresh])

  const renderGraph = () => {
    return (
      <Grid container>
        <Grid item xs={4}>
          <div onClick={() => onChangeTable(TableType.TRANSACTIONS)}>
            <TransactionsChart />
          </div>
        </Grid>
        <Grid item xs={4}>
          <div onClick={() => onChangeTable(TableType.SALE_VOLUME)}>
            <SalesVolumeChart />
          </div>
        </Grid>
        <Grid item xs={4}>
          <div onClick={() => onChangeTable(TableType.DISPUTES)}>
            <DisputeChart />
          </div>
        </Grid>
      </Grid>
    )
  }

  return (
    <Fragment>
      <div className="row">
        <div className="col-md-6 d-flex justify-content-start">
          <div className="bg-transparent border-light p-3">
            <SectionTitle title="Sales" />
            <span className="k-mx-2 k-cursor-pointer" onClick={() => codatConnected && setOnRefresh(true)}>
              <FontAwesomeIcon icon={faSync} color="LightGray" />
            </span>
          </div>
        </div>
        <div className="col-md-6 d-flex justify-content-end cursor-pointer">
          <CloseIcon
            onClick={() => {
              dispatch({
                type: actionTypes.SETSALESOVERVIEWDAYSPAN,
                data: 30,
              })
              dispatch({ type: actionTypes.HIDEDIALOG })
            }}
            fontSize="medium"
            sx={{
              cursor: 'pointer',
              position: 'absolute',
              top: '20px',
              right: '20px',
            }}
          />
        </div>
      </div>
      {onRefresh && <AlertBadge top="20px" onClose={() => setOnRefresh(false)} />}
      <div className="k-d-flex k-justify-content-between k-align-items-center k-py-3 color-primary">
        <strong>Sales Overview</strong>

        <DaySpan onClick={onDaySpanClicked} daySpanSelected={dayspanSelected} />
      </div>

      {!codatConnected && <SalesOverviewPlaceholder />}
      {!codatConnected && (
        <div className="bg-transparent border-light align-text-bottom text-center py-3">
          <ConnectButton label="Connect your sales application" isConnected={codatConnected} />

          <p className="p-3 disclaimer-text">
            When you connect your sales application you will see your past and current stats.
          </p>
        </div>
      )}

      {codatConnected && (
        <div className="mb-3">
          {renderGraph()}

          {activeTableType === TableType.DISPUTES && (
            <DisputesTable
              filterState={disputesFilter}
              setFilterState={setDisputesFilter}
              tableType={activeTableType}
              onChangeTable={onChangeTable}
            />
          )}

          {activeTableType === TableType.TRANSACTIONS && (
            <TransactionTable
              filterState={trxFilter}
              setFilterState={setTrxFilter}
              tableType={activeTableType}
              onChangeTable={onChangeTable}
            />
          )}

          {activeTableType === TableType.SALE_VOLUME && (
            <TransactionTable tableType={activeTableType} onChangeTable={onChangeTable} />
          )}
        </div>
      )}

      <div className="dialog-markaaz-payment-container position-relative text-center">
        <div className="markaaz-payments-link-container">
          <p className="p-3" style={{ color: '#25245f' }}>
            <span className="px-1">
              <b>COMING SOON:</b>
            </span>
            track your sales KPIs here.
            <span className="px-1">
              <Link
                rel="noreferrer noopener"
                href="https://markaaz.com/blog/"
                target="_blank"
                underline="hover"
                className="markaaz-payments-link"
              >
                <Typography component="span" variant="body2B" color="secondary">
                  Learn more about Sales KPIs we can track!
                </Typography>
              </Link>
            </span>
          </p>
          <div className="link-hover-message">
            Make sure you reach your 2022 goals. Soon you will be able to track your actuals against your goals.
          </div>
        </div>
      </div>

      <ZendeskLink />
    </Fragment>
  )
}

export default SalesDialog
